import React from 'react';
import { Layers } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import Stepper from './Stepper';

/**
 * Generic, format-agnostic grouping step — shown for ANY format that declares
 * needsGrouping (group_knockout, regional_bracket, future group-based formats
 * slot in without a new component). Deliberately does not ask about
 * cross-group vs same-group matchups — that pairing logic is a property of
 * the format's own generator, not something an organizer configures by hand.
 *
 * The "advance per group" stepper only shows when the format's advancement
 * is actually configurable (group_knockout: top N by rank). Formats where
 * advancement is always exactly the group's winner — no rank choice possible
 * (regional_bracket: it's a single-elim bracket per region, there's only
 * ever one winner) — set `format.fixedAdvancePerGroup` and the stepper hides.
 * Formats with no "advance" concept at all — groups don't feed into
 * anything, they just play each other directly (cross_group) — set
 * `format.hideAdvanceStep` and the whole block (stepper AND "Fixed" caption)
 * is skipped, since neither describes what actually happens.
 *
 * The group COUNT itself is only adjustable when it actually scales with
 * participant count (group_knockout/regional_bracket: more entrants, more
 * pools/regions makes sense). A "dual" format is always exactly 2 sides
 * regardless of how many teams are on each side — `format.fixedGroupCount`
 * locks the stepper into the same "Fixed" caption treatment.
 */
const GroupingStep = ({ format, participantCount, groupingConfig, onChange }) => {
  const { theme } = useTheme();
  if (!groupingConfig) return null;

  const unitLabel = format.groupUnitLabel || 'Group';
  const unitLabelPlural = `${unitLabel}s`;
  const showAdvanceStepper = !format.fixedAdvancePerGroup && !format.hideAdvanceStep;
  const showGroupCountStepper = !format.fixedGroupCount;

  const maxGroups = Math.max(2, Math.floor(participantCount / 2));
  const maxAdvance = Math.max(1, Math.floor(participantCount / groupingConfig.groupCount));

  return (
    <div className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-4`}>
      <div className={`mb-3 flex items-center gap-2 text-sm font-semibold ${theme.text}`}>
        <Layers size={16} />
        {unitLabelPlural}
      </div>
      <div className="space-y-3">
        {showGroupCountStepper ? (
          <Stepper
            hint={`~${Math.round(participantCount / groupingConfig.groupCount)} participants per ${unitLabel.toLowerCase()}`}
            value={groupingConfig.groupCount}
            min={2}
            max={maxGroups}
            onChange={(groupCount) => onChange({ ...groupingConfig, groupCount, userAdjustedGroups: true })}
          />
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className={`text-sm font-medium ${theme.text}`}>Always exactly {format.fixedGroupCount} sides</div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${theme.backgroundSecondary} ${theme.textMuted}`}>
              Fixed
            </span>
          </div>
        )}
        {showAdvanceStepper ? (
          <Stepper
            label={`Advance per ${unitLabel.toLowerCase()}`}
            hint="Move into the knockout bracket"
            value={groupingConfig.advancePerGroup}
            min={1}
            max={Math.max(1, maxAdvance)}
            onChange={(advancePerGroup) => onChange({ ...groupingConfig, advancePerGroup })}
          />
        ) : !format.hideAdvanceStep ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>Advances</div>
              <div className={`text-xs ${theme.textMuted}`}>Only the {unitLabel.toLowerCase()}'s own winner</div>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${theme.backgroundSecondary} ${theme.textMuted}`}>
              Fixed
            </span>
          </div>
        ) : null}
      </div>
      <p className={`mt-3 text-xs ${theme.textMuted}`}>
        {format.hideAdvanceStep
          ? `Every team faces every team in the other ${unitLabel.toLowerCase()}(s), never its own — nothing else to set here.`
          : `Cross-${unitLabel.toLowerCase()} vs. same-${unitLabel.toLowerCase()} matchups are handled automatically by ${format.label} — nothing to set here.`}
      </p>
    </div>
  );
};

export default GroupingStep;
